import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    BindInfo,
    CheckResult,
    ConnectionsInputPort,
    ConnectionsOutputPort,
    GetExperimentList,
    GetMonitorMatrix,
    GetOutputAttenuation,
    LinkPort,
    ListMatrixLogs,
    Matrix,
    MatrixAllEditMatrixData,
    MatrixCreate,
    MatrixDelete,
    MatrixEdit,
    MatrixEditMatrixData,
    MatrixListParams,
    MatrixLog,
    MatrixMonitor,
    MatrixPortsChamberAssign,
    MatrixPortsInputUser,
    MatrixPortsRadioAssign,
    MatrixSwitchStatus,
    MatrixWithPage,
} from '@app/interfaces/rm/matrix';
import { HardwareType, MonitorData } from '@app/interfaces/rm/monitor';
import { OptionsParams, SelectOption } from '@app/interfaces/sys/dict';
import { environment } from '@env/environment';
import dayjs from 'dayjs';
import { lastValueFrom, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class MatrixHttp {
    private moduleApi = environment.api.services.rm;
    private baseUrl = this.moduleApi + '/matrices';

    constructor(private http: HttpClient) {
    }

    async matrixList(option: MatrixListParams): Promise<MatrixWithPage> {
        return lastValueFrom(this.http.get<MatrixWithPage>(this.baseUrl, {
            params: Object.assign(option),
        }));
    }

    async detail(params: { id: number }): Promise<Matrix> {
        return lastValueFrom(this.http.get<Matrix>(`${this.baseUrl}/one`, {
            params: Object.assign(params),
        }));
    }

    async options(params?: OptionsParams): Promise<SelectOption[]> {
        return lastValueFrom(this.http.get<SelectOption[]>(`${this.baseUrl}/all-options`, {
            params: params ? Object.assign(params) : {}
        }));
    }

    async matrixCreate(option: MatrixCreate): Promise<void> {
        return lastValueFrom(this.http.post<void>(this.baseUrl, option));
    }

    async matrixDelete(option: MatrixDelete): Promise<void> {
        return lastValueFrom(this.http.request<void>('DELETE', this.baseUrl, {
            body: Object.assign(option),
        }));
    }

    async matrixTestRunning(option: { matrixId: number }): Promise<CheckResult> {
        return lastValueFrom(this.http.get<CheckResult>(`${this.baseUrl}/experiment-status`, {
            params: Object.assign(option),
        }));
    }

    async matrixPositiveStopped(option: { matrixId: number }): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/status/force-stop?matrixId=${option.matrixId}`, option));
    }

    async matrixEdit(option: MatrixEdit): Promise<void> {
        return lastValueFrom(this.http.put<void>(this.baseUrl, option));
    }

    async matrixSwitchStatus(option: MatrixSwitchStatus): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/switch-status`, option));
    }

    async matrixPortsInputUser(option: MatrixPortsInputUser): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-ports/input-user`, option));
    }

    async matrixPortsChamberAssign(option: MatrixPortsChamberAssign): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-ports/connections/chamber`, option));
    }

    async matrixPortsRadioAssign(option: MatrixPortsRadioAssign): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-ports/connections/radio`, option));
    }

    async matrixEditDesc(option: { id: number; description: string | null }): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-ports/description`, option));
    }

    async matrixBindInfo(option: { id: number; type: string }): Promise<BindInfo> {
        return lastValueFrom(this.http.get<BindInfo>(`${this.moduleApi}/matrix-ports/connections`, {
            params: Object.assign(option),
        }))
    }

    // monitor http
    async monitorMatrixList(): Promise<Matrix[]> {
        return lastValueFrom(this.http.get<Matrix[]>(`${this.baseUrl}/running`));
    }

    async getHardwareType(): Promise<HardwareType[]> {
        return lastValueFrom(this.http.get<HardwareType[]>(`${this.baseUrl}/hardware-types`));
    }

    async matrixMonitor(option: MatrixMonitor): Promise<MonitorData> {
        return lastValueFrom(this.http.get<MonitorData>(`${this.moduleApi}/matrix-data/monitor`, {
            params: Object.assign(option),
        }))
    }

    async matrixEditMatrixData(option: MatrixEditMatrixData): Promise<any> {
        return lastValueFrom(this.http.put(`${this.moduleApi}/matrix-data/attenuation`, option));
    }

    async matrixEditMatrixBtn(option: MatrixEditMatrixData): Promise<any> {
        return lastValueFrom(this.http.put(`${this.moduleApi}/matrix-data/on-off`, option));
    }

    async matrixAllEditMatrixData(option: MatrixAllEditMatrixData): Promise<any> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-data/max-attenuation?matrixId=${option.matrixId}&chamberId=${option.chamberId}`, option));
    }

    async getMonitorChamber(): Promise<any> {
        return lastValueFrom(this.http.get(`${this.moduleApi}/chambers/list`));
    }

    async getMonitorMatrix(option: GetMonitorMatrix): Promise<LinkPort[]> {
        return lastValueFrom(this.http.get<LinkPort[]>(`${this.baseUrl}/chamber-connected`, {
            params: Object.assign(option),
        }));
    }

    async getOutputAttenuation(option: GetOutputAttenuation): Promise<any> {
        return lastValueFrom(this.http.get(`${this.moduleApi}/matrix-data/attenuation`, {
            params: Object.assign(option),
        }));
    }

    async selectedOutputAttenuation(option: GetOutputAttenuation): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-data/selected`, option));
    }

    async getExperimentList(option: GetExperimentList): Promise<any> {
        return lastValueFrom(this.http.get(`${this.moduleApi}/matrix-data/radio-testing`, {
            params: Object.assign(option),
        }));
    }

    getExperimentListSubscribe(option: GetExperimentList): Observable<any> {
        return this.http.get(`${this.moduleApi}/matrix-data/radio-testing`, {
            params: Object.assign(option)
        });
    }

    async listMatrixLogs(option: ListMatrixLogs): Promise<MatrixLog[]> {
        return lastValueFrom(this.http.get<MatrixLog[]>(`${this.baseUrl}/logs`, {
            params: Object.assign(option),
        }));
    }

    async connectionsInputPort(option: ConnectionsInputPort): Promise<any> {
        return lastValueFrom(this.http.get(`${this.moduleApi}/matrix-ports/connections/input`, {
            params: Object.assign(option),
        }));
    }

    async connectionsOutputPort(option: ConnectionsOutputPort): Promise<any> {
        return lastValueFrom(this.http.get(`${this.moduleApi}/matrix-ports/connections/output`, {
            params: Object.assign(option),
        }));
    }

    async connectionExport(matrixId: number): Promise<void> {
        return lastValueFrom(this.http.get<void>(`${this.moduleApi}/matrix-ports/export?matrixId=${matrixId}`));
    }

    async importPortDescriptionPre(matrixId: number, data: FormData): Promise<any> {
        return lastValueFrom(this.http.post<void>(`${this.moduleApi}/matrix-ports/import/pre-check?matrixId=${matrixId}`, data));
    }

    async importPortDescription(matrixId: number, data: FormData): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-ports/import?matrixId=${matrixId}`, data));
    }

    async importMatrixData(matrixId: string | number, data: FormData): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/matrix-data/import?matrixId=${matrixId}`, data));
    }

    exportPortDescription(matrixId: number): void {
        const tz = dayjs().format('ZZ');
        const url = `${environment.api.baseUrl}${this.moduleApi}/matrix-ports/export?matrixId=${matrixId}&tz=${encodeURIComponent(tz)}`;
        console.log('url', url);
        window.open(url);
    }

    downloadMatrixLog(fileUrl: string): void {
        const url = `${environment.api.baseUrl}${this.baseUrl}/logs/export?path=${fileUrl}`;
        window.open(url);
    }

    downloadMatrixData(matrixId: number): void {
        const tz = dayjs().format('ZZ');
        const url = `${environment.api.baseUrl}${this.moduleApi}/matrix-data/export?matrixId=${matrixId}&tz=${encodeURIComponent(tz)}`;
        window.open(url);
    }
}
